import {
  BatchError,
  ClientTypes,
  CreateFilesResponse,
  DuplicateBehavior,
  Fields,
  FileAccessLevel,
  FileChannel,
  FileResponse,
  FileSaveModel,
} from 'src/store/files/types';
import { API } from 'src/utils/AmplifyApiUtils';

const appApi = 'AppAPI';
const url = '/files/';

// Input type for file update operations - only requires id and fields
export interface FileUpdateInput {
  id: string;
  clientPermissions?: FileAccessLevel;
  fields: Partial<Fields>;
}

// Response type for file update operations
export interface UpdateFilesResponse {
  succeededIds: string[];
  failedIds: string[];
  unauthorizedIds?: string[];
  updatedItems?: FileResponse[];
  errors?: BatchError[];
  // Incoming folder IDs that were merged into an existing folder at the
  // destination (folder-level Replace = Merge). Consumers should remove these
  // IDs from local state — the existing destination folder absorbs the move.
  mergedIds?: string[];
}

export interface FileBundleData {
  key: string;
  name: string;
  path: string;
  identityID: string;
  /** External link target when present — BE skips these (no S3 object). */
  url?: string;
}

export interface CreateChannelData {
  companyID: string;
  clientUserIDs: string[];
  singleClientId?: string;
  membershipType: string;
}

export const getOwnerIdAndTypeFromFileChannelId = (fileChannelId: string) => {
  const [, companyId, userId] = fileChannelId.split('/');
  return userId === 'none'
    ? { ownerId: companyId, ownerType: ClientTypes.CLIENT_COMPANY }
    : { ownerId: userId, ownerType: ClientTypes.CLIENT_USER };
};

interface GetZippedBundleInput {
  fileData: FileBundleData[];
  bundleName: string;
}

interface FileMetadataResponse {
  data: FileResponse[];
  nextToken: string;
}

export default class FilesClient {
  /**
   * Return file entities based on channel
   * @param channelID which channel to look for files
   */
  static async GetFilesForChannelId(
    channelID: string,
    nextToken: string = '',
    files: FileResponse[] = [],
  ): Promise<FileResponse[]> {
    const { ownerId, ownerType } =
      getOwnerIdAndTypeFromFileChannelId(channelID);
    const response: FileMetadataResponse = await API.get(appApi, url, {
      queryStringParameters: {
        channelID,
        ownerType,
        ownerId,
        limit: 1000,
        nextToken,
      },
    });
    files.push(...response.data);
    if (response.nextToken) {
      return this.GetFilesForChannelId(channelID, response.nextToken, files);
    }
    return files;
  }

  /**
   * Upload file entities to a channel, this can be a folder, link, uploaded file
   * @param channelID which channel this file is for
   * @param files file items to create
   * @param duplicateBehavior optional behavior for handling duplicate filenames
   */
  static CreateFilesInChannel(
    channelID: string,
    files: Array<FileSaveModel>,
    duplicateBehavior?: DuplicateBehavior,
  ): Promise<CreateFilesResponse> {
    const filesList = files.map((file) => ({
      fields: {
        // Omit empty key so CreateFileMetadata treats the row as metadata-first
        // (FormatPathAndKey + pending + uploadUrl). Folders keep __placeholder__.
        ...(file.key ? { fileKey: file.key } : {}),
        starred: false,
        tags: [],
        path: file.path || '',
        fileUrl: file.url || '',
        ...(duplicateBehavior && { duplicateBehavior }),
      },
    }));

    return API.post(appApi, url, {
      queryStringParameters: {
        channelID,
      },
      body: {
        data: filesList,
      },
    });
  }

  static updateFiles(
    isMoveAction = false,
    duplicateBehavior?: DuplicateBehavior,
  ) {
    return (targetFiles: FileUpdateInput[]): Promise<UpdateFilesResponse> =>
      API.put(appApi, url, {
        body: {
          data: targetFiles.map((file) => ({
            ...file,
            fields: {
              ...file.fields,
              ...(duplicateBehavior && { duplicateBehavior }),
            },
          })),
        },
        ...(isMoveAction && {
          queryStringParameters: {
            isMoveAction: true,
          },
        }),
      });
  }

  static getZippedBundle(input: GetZippedBundleInput) {
    return API.put(appApi, `/files/download`, {
      body: {
        fileData: input.fileData,
        bundleName: input.bundleName,
      },
    });
  }

  static getDownloadProgress(key: string) {
    return API.get(appApi, `/files/download/progress`, {
      queryStringParameters: {
        key,
      },
    });
  }

  static batchDownload(input: {
    fileIds: string[];
    folderIds: string[];
  }): Promise<string> {
    return API.post(appApi, `/files/batch-download`, {
      body: {
        fileIds: input.fileIds,
        folderIds: input.folderIds,
      },
    });
  }

  static getBatchDownloadProgress(key: string) {
    return API.get(appApi, `/files/batch-download/progress`, {
      queryStringParameters: {
        key,
      },
    });
  }

  static batchDelete(input: {
    fileIds: string[];
    folderIds: string[];
  }): Promise<string> {
    return API.post(appApi, `/files/batch-delete`, {
      body: {
        fileIds: input.fileIds,
        folderIds: input.folderIds,
      },
    });
  }

  static getBatchDeleteProgress(key: string) {
    return API.get(appApi, `/files/batch-delete/progress`, {
      queryStringParameters: {
        key,
      },
    });
  }

  static createChannel(data: CreateChannelData) {
    return API.post(appApi, `/entities/FILE_CHANNEL`, {
      body: data,
    });
  }

  static deleteFileChannel(id: string) {
    return API.del('AppAPI', '/entities/FILE_CHANNEL', {
      body: [id],
    });
  }

  static updateFileChannel(fileChannel: FileChannel) {
    return API.put(appApi, `/entities/FILE_CHANNEL`, {
      body: {
        ...fileChannel,
      },
    });
  }

  static updateRootFolderPermissions(
    fileChannelID: string,
    clientPermissions: FileAccessLevel,
  ) {
    return API.put(appApi, `/files/root/permissions`, {
      body: {
        channelId: fileChannelID,
        clientPermissions: clientPermissions,
      },
    });
  }

  static updateFolderPermissions(
    fileId: string,
    clientPermissions: FileAccessLevel,
  ) {
    return API.put(appApi, `/files/${fileId}/permissions`, {
      body: {
        clientPermissions,
      },
    });
  }
}
